import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Image,
  Pressable,
} from 'react-native';
import { useForm, Controller } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigation, useRoute } from '@react-navigation/native';
import { palette } from '../../utils/palette';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RouteProp } from '@react-navigation/native';
import TextField from '../../components/common/TextField';
import Button from '../../components/common/Button';
import EyeIcon from '../../components/common/EyeIcon';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useAuth } from '../../hooks/useAuth';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import Text from '../../components/common/Text';

// Dynamic schema based on mode
const createSchema = (mode: 'change' | 'forgot') => {
  const baseSchema = {
    newPassword: z
      .string()
      .min(6, 'New password must be at least 6 characters'),
    confirmNewPassword: z.string(),
  };

  if (mode === 'change') {
    return z
      .object({
        currentPassword: z.string().min(6, 'Current password is required'),
        ...baseSchema,
      })
      .refine((data) => data.newPassword === data.confirmNewPassword, {
        message: "Passwords don't match",
        path: ['confirmNewPassword'],
      });
  } else {
    // Forgot mode: only email - backend sends reset link; user sets password via email link
    return z.object({
      email: z.string().email('Invalid email address'),
    });
  }
};

type FormData = z.infer<ReturnType<typeof createSchema>>;

const PasswordScreen: React.FC = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, 'ChangePassword'>>();
  const mode = route.params?.mode || 'change';
  const email = route.params?.email;
  const schema = createSchema(mode);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      email: email || '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isMobile, screenWidth } = useResponsiveLayout();
  const { changePassword, forgotPassword } = useAuth();
  const { colors } = useTheme();

  // Responsive values
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const logoSize = isMobile ? 40 : isTablet ? 50 : 60;
  const formPadding = isMobile ? 24 : isTablet ? 40 : 50;

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (mode === 'change') {
        const changeData = data as {
          currentPassword: string;
          newPassword: string;
          confirmNewPassword: string;
        };
        await changePassword({
          currentPassword: changeData.currentPassword,
          newPassword: changeData.newPassword,
        });
      } else {
        // Forgot mode: only email is needed - backend sends reset link (secure flow)
        const forgotData = data as {
          email: string;
          newPassword: string;
          confirmNewPassword: string;
        };
        await forgotPassword({ email: forgotData.email });
      }
      setLoading(false);
      setSuccess(true);

      // Redirect to login page after a short delay to show success message
      setTimeout(() => {
        navigation.navigate('Login');
      }, 2000);
    } catch (e: unknown) {
      setLoading(false);
      setError(
        e instanceof Error
          ? e.message
          : 'An error occurred. Please try again later.'
      );
    }
  };

  const getTitle = () => {
    return mode === 'change' ? 'Change Password' : 'Reset Password';
  };

  const getSubtitle = () => {
    return mode === 'change'
      ? 'Enter your current and new password below'
      : "Enter your email and we'll send you a link to reset your password";
  };

  const getButtonTitle = () => {
    return mode === 'change' ? 'Change Password' : 'Send reset link';
  };

  const getSuccessMessage = () => {
    return mode === 'change'
      ? 'Password changed successfully!'
      : 'Check your email for a link to reset your password.';
  };

  return (
    <ResponsiveContainer
      style={[styles.container, { backgroundColor: colors.authBackground }]}
    >
      <KeyboardAvoidingView
        style={[styles.keyboardAvoidingView, { zIndex: 10 }]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContentContainer}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          bounces={false}
        >
          <View
            style={[
              styles.contentContainer,
              !isMobile && styles.webContentContainer,
            ]}
          >
            {/* Professional Form Card */}
            <View
              style={[
                styles.formContainer,
                {
                  backgroundColor: colors.card,
                  shadowColor: colors.border,
                  borderColor: colors.borderLight,
                  paddingHorizontal: formPadding,
                },
              ]}
            >
              {/* Form Header */}
              <View style={styles.formHeader}>
                <Pressable
                  onPress={() => navigation.navigate('Landing')}
                  style={({ focused }) => [
                    styles.logoContainer,
                    Platform.OS === 'web' &&
                      focused && {
                        outlineWidth: 2,
                        outlineStyle: 'solid',
                        outlineColor: colors.theme_color || colors.primary,
                        outlineOffset: 2,
                      },
                  ]}
                >
                  <Image
                    source={require('../../../assets/logo-square.png')}
                    style={{ width: logoSize, height: logoSize }}
                    resizeMode="contain"
                  />
                </Pressable>
                <Text
                  size="md"
                  weight="bold"
                  style={[styles.formTitle, { color: colors.primary }]}
                >
                  {getTitle()}
                </Text>
                <Text
                  size="sm"
                  style={[
                    styles.formSubtitle,
                    { color: colors.secondary, fontSize: isMobile ? 14 : 17 },
                  ]}
                >
                  {getSubtitle()}
                </Text>
              </View>
              <View style={styles.form}>
                {mode === 'change' && (
                  <Controller
                    control={control}
                    name="currentPassword"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextField
                        label="Current Password"
                        placeholder="Enter current password"
                        secureTextEntry={!showCurrent}
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        error={errors.currentPassword?.message}
                        rightIcon={<EyeIcon isVisible={showCurrent} />}
                        onRightIconPress={() => setShowCurrent(!showCurrent)}
                        autoCapitalize="none"
                      />
                    )}
                  />
                )}

                {mode === 'forgot' && (
                  <Controller
                    control={control}
                    name="email"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextField
                        label="Email"
                        placeholder="Enter your email"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        error={errors.email?.message}
                      />
                    )}
                  />
                )}

                {mode === 'change' && (
                  <>
                    <Controller
                      control={control}
                      name="newPassword"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextField
                          label="New Password"
                          placeholder="Enter new password"
                          secureTextEntry={!showNew}
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          error={errors.newPassword?.message}
                          rightIcon={<EyeIcon isVisible={showNew} />}
                          onRightIconPress={() => setShowNew(!showNew)}
                          autoCapitalize="none"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="confirmNewPassword"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextField
                          label="Confirm New Password"
                          placeholder="Confirm new password"
                          secureTextEntry={!showConfirm}
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          error={errors.confirmNewPassword?.message}
                          rightIcon={<EyeIcon isVisible={showConfirm} />}
                          onRightIconPress={() => setShowConfirm(!showConfirm)}
                          autoCapitalize="none"
                        />
                      )}
                    />
                  </>
                )}
              </View>

              {/* Error Display */}
              {error && (
                <View
                  style={[
                    styles.errorContainer,
                    { backgroundColor: colors.errorBackground },
                  ]}
                >
                  <Text
                    size="sm"
                    weight="medium"
                    style={[styles.errorText, { color: colors.errorText }]}
                  >
                    {error}
                  </Text>
                </View>
              )}

              {/* Success Display */}
              {success && (
                <View
                  style={[
                    styles.successContainer,
                    { backgroundColor: colors.success + '20' },
                  ]}
                >
                  <Text
                    size="sm"
                    weight="medium"
                    style={[styles.successText, { color: colors.success }]}
                  >
                    {getSuccessMessage()}
                  </Text>
                </View>
              )}

              {/* Button */}
              <Button
                title={getButtonTitle()}
                onPress={handleSubmit(onSubmit)}
                loading={loading}
                style={styles.button}
                trackClick={true}
                trackingLocation="change-password-screen"
                trackingData={{
                  button_name: getButtonTitle(),
                  button_type: 'change_password',
                  screen: 'change_password',
                }}
              />

              {/* Legal Links */}
              <View style={styles.legalLinks}>
                <Pressable
                  onPress={() => navigation.navigate('TermsOfService')}
                  hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                  style={({ focused }) =>
                    Platform.OS === 'web' && focused
                      ? {
                          outlineWidth: 2,
                          outlineStyle: 'solid',
                          outlineColor: colors.theme_color || colors.primary,
                          outlineOffset: 2,
                          borderRadius: 4,
                        }
                      : {}
                  }
                >
                  <Text
                    size="xs"
                    style={[styles.legalText, { color: colors.primary }]}
                  >
                    Terms of Use
                  </Text>
                </Pressable>
                <View style={styles.separatorContainer}>
                  <Text
                    size="xs"
                    style={[styles.legalText, { color: colors.secondary }]}
                  >
                    {' | '}
                  </Text>
                </View>
                <Pressable
                  onPress={() => navigation.navigate('PrivacyPolicy')}
                  hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                  style={({ focused }) =>
                    Platform.OS === 'web' && focused
                      ? {
                          outlineWidth: 2,
                          outlineStyle: 'solid',
                          outlineColor: colors.theme_color || colors.primary,
                          outlineOffset: 2,
                          borderRadius: 4,
                        }
                      : {}
                  }
                >
                  <Text
                    size="xs"
                    style={[styles.legalText, { color: colors.primary }]}
                  >
                    Privacy Policy
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }
      : {}),
  },
  scrollView: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    minHeight: '100%',
    maxHeight: '100%',
  },
  contentContainer: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
  },
  formContainer: {
    borderRadius: 20,
    paddingHorizontal: 50,
    paddingVertical: 35,
    minHeight: 320,
    shadowOffset: {
      width: 0,
      height: 12,
    },
    shadowOpacity: 0.3,
    shadowRadius: 25,
    elevation: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  formHeader: {
    marginBottom: 12,
    alignItems: 'center',
  },
  logoContainer: {
    marginBottom: 8,
    padding: 8,
    borderRadius: 12,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineColor: 'transparent',
      outlineStyle: 'solid',
    }),
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  formTitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 4,
    letterSpacing: -0.8,
    textShadowColor: 'rgba(0, 0, 0, 0.1)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  formSubtitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    opacity: 0.8,
    fontWeight: '500',
    letterSpacing: 0.2,
    marginTop: 4,
  },
  form: {
    marginBottom: 12,
    gap: 2,
  },
  errorContainer: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: palette.brightMatteRed,
  },
  errorText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    textAlign: 'center',
  },
  successContainer: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: palette.cyanLimeGreen,
  },
  successText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    textAlign: 'center',
  },
  button: {
    marginBottom: 24,
    height: 52,
    borderRadius: 16,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  legalLinks: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    flexWrap: 'nowrap',
  },
  separatorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  legalText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontWeight: '400',
    opacity: 0.7,
  },
  webContentContainer: {
    width: '55%',
    alignSelf: 'center',
    maxWidth: 520,
    minWidth: 400,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
});

export default PasswordScreen;
